import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormattedLocationString } from '@eventbrite/consumer-search.event-renderer';
import { useAppContext } from '@/components/_shared/context/AppContext';

import { SignalBadgeStatus } from '@/components/_shared/SignalBadge';
import { getDisplayTimezone, getFormattedDateOnlyLocaleBased } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import type { RelatedEvent, UrgencySignals, Venue } from '../../types';

// Type assertion helper for external library compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalVenue = any;

export interface FormattedEventData {
  id?: string;
  isFree: boolean;
  imageUrl: string;
  isOnlineEvent: boolean;
  isPromoted: boolean;
  locationString: string;
  formattedPriceString: string;
  name?: string;
  urgencySignals: UrgencySignals;
  url: string;
  venue: Venue | undefined;
  formattedEventDateTimeString: string;
  urgencySignalStatus: SignalBadgeStatus | null;
  priceString?: string;
}

const formatOnlineEventLocation = (
  formatMessage: (descriptor: { id: string; defaultMessage?: string; description?: string }) => string,
): string => {
  return formatMessage({
    id: 'Online event',
    defaultMessage: 'Online event',
    description: 'Label for online event location',
  });
};

const formatInPersonEventLocation = (locationStringFromHook: string): string => {
  return locationStringFromHook;
};

const formatDateTimeString = (
  startDate: string | undefined,
  startTime: string | undefined,
  timezone: string,
  locale: string,
  isOnlineEvent: boolean,
  attendeeTimezone?: string,
): string => {
  if (!startDate) return '';

  const timeString = startTime ?? '00:00:00';
  const utcDateString = `${startDate}T${timeString}Z`;
  const dateObj = new Date(utcDateString);

  const targetTimezone = getDisplayTimezone({
    isOnlineEvent,
    eventTimezone: timezone,
    attendeeTimezone,
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    // Use 12h format (AM/PM) for en-US, 24h format for all other locales
    hour12: locale === 'en-US',
    timeZone: targetTimezone,
  });

  const dateWithWeekday = getFormattedDateOnlyLocaleBased(dateObj, {
    locale,
    timezone: targetTimezone,
    monthFormat: 'long',
    showYear: false,
    weekday: 'short',
  });
  const time = timeFormatter.format(dateObj);

  // Construct final format: "Mon, May 5 • 11:30 AM" or "Lun, 5 Mayo • 11:30"
  return `${dateWithWeekday} • ${time}`;
};

const getUrgencySignalStatus = (urgencySignals: UrgencySignals | undefined): SignalBadgeStatus | null => {
  if (!urgencySignals) return null;

  const trueUrgencySignals = Object.keys(urgencySignals).filter(
    (key) => urgencySignals[key as keyof UrgencySignals] === true,
  );

  return trueUrgencySignals.length > 0 ? (trueUrgencySignals[0] as SignalBadgeStatus) : null;
};

const formatPriceString = (
  isFree: boolean,
  formattedPriceString: string | undefined,
  formatMessage: (descriptor: { id: string }, values?: Record<string, string>) => string,
): string | undefined => {
  if (!isFree && formattedPriceString) {
    return formatMessage({ id: 'Starts at {formattedMinPrice}' }, { formattedMinPrice: formattedPriceString });
  }
  return undefined;
};

const buildFormattedEventData = (
  relatedEvent: RelatedEvent,
  locationString: string,
  formattedEventDateTimeString: string,
  urgencySignalStatus: SignalBadgeStatus | null,
  priceString: string | undefined,
): FormattedEventData => ({
  id: relatedEvent.id,
  isFree: relatedEvent.isFree ?? false,
  imageUrl: relatedEvent.imageUrl ?? '',
  isOnlineEvent: relatedEvent.isOnlineEvent ?? false,
  isPromoted: relatedEvent.isPromoted ?? false,
  locationString,
  formattedPriceString: relatedEvent.formattedPriceString ?? '',
  name: relatedEvent.name,
  urgencySignals: relatedEvent.urgencySignals ?? {},
  url: relatedEvent.url ?? '#',
  venue: relatedEvent.venue,
  formattedEventDateTimeString,
  urgencySignalStatus,
  priceString,
});

export const useFormattedEventData = (relatedEvent: RelatedEvent | null): FormattedEventData | null => {
  const { formatMessage } = useIntl();
  const attendeeTimezone = useAttendeeTimezone(relatedEvent?.isOnlineEvent ?? false);
  const { data } = useAppContext();
  const locale = useMemo(() => {
    return data.locale || navigator.language;
  }, [data.locale]);

  // External hooks - must be called unconditionally
  const locationStringFromHook = useFormattedLocationString({
    venue: relatedEvent?.venue as ExternalVenue,
    style: 'full',
  });

  const formattedData = useMemo((): FormattedEventData | null => {
    if (!relatedEvent) {
      return null;
    }

    const locationString = relatedEvent.isOnlineEvent
      ? formatOnlineEventLocation(formatMessage)
      : formatInPersonEventLocation(locationStringFromHook);

    const formattedEventDateTimeString = formatDateTimeString(
      relatedEvent.startDate,
      relatedEvent.startTime,
      relatedEvent.timezone ?? 'UTC',
      locale,
      relatedEvent.isOnlineEvent ?? false,
      attendeeTimezone,
    );

    const urgencySignalStatus = getUrgencySignalStatus(relatedEvent.urgencySignals);

    const priceString = formatPriceString(
      relatedEvent.isFree ?? false,
      relatedEvent.formattedPriceString,
      formatMessage,
    );

    return buildFormattedEventData(
      relatedEvent,
      locationString,
      formattedEventDateTimeString,
      urgencySignalStatus,
      priceString,
    );
  }, [relatedEvent, formatMessage, locationStringFromHook, attendeeTimezone]);

  return formattedData;
};
